import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Trash2, Mail, Phone, Calendar, Users, Search, RefreshCw } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const LOGO = "https://customer-assets.emergentagent.com/job_ff078f22-bc2b-467a-b845-6483b385dad8/artifacts/c2lq75mw_image.png";

const STATUSES = [
    { value: "nouveau", label: "Nouveau", className: "bg-kayama-pink text-white" },
    { value: "en_cours", label: "En cours", className: "bg-kayama-gold text-white" },
    { value: "traite", label: "Traité", className: "bg-emerald-600 text-white" },
    { value: "archive", label: "Archivé", className: "bg-kayama-brown-3 text-white" },
];

function StatusBadge({ status }) {
    const s = STATUSES.find((x) => x.value === status) || STATUSES[0];
    return <span className={`inline-block text-[10px] uppercase tracking-[0.18em] px-3 py-1 ${s.className}`}>{s.label}</span>;
}

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
}

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, by_status: {} });
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const [list, st] = await Promise.all([
                api.get("/admin/devis"),
                api.get("/admin/stats"),
            ]);
            setItems(list.data);
            setStats(st.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    function handleLogout() {
        logout();
        nav("/admin/login");
    }

    async function changeStatus(id, status) {
        try {
            const { data } = await api.patch(`/admin/devis/${id}/status`, { status });
            setItems((arr) => arr.map((it) => (it.id === id ? data : it)));
            if (selected?.id === id) setSelected(data);
            toast.success("Statut mis à jour");
            // refresh stats
            const st = await api.get("/admin/stats");
            setStats(st.data);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function remove(id) {
        if (!window.confirm("Supprimer cette demande définitivement ?")) return;
        try {
            await api.delete(`/admin/devis/${id}`);
            setItems((arr) => arr.filter((it) => it.id !== id));
            setSelected(null);
            toast.success("Demande supprimée");
            const st = await api.get("/admin/stats");
            setStats(st.data);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    const filtered = items.filter((it) => {
        if (filter !== "all" && it.status !== filter) return false;
        if (search) {
            const q = search.toLowerCase();
            return (
                it.name.toLowerCase().includes(q) ||
                it.email.toLowerCase().includes(q) ||
                it.event_type.toLowerCase().includes(q)
            );
        }
        return true;
    });

    return (
        <div className="min-h-screen bg-kayama-cream" data-testid="page-admin-dashboard">
            {/* Top bar */}
            <header className="bg-kayama-paper border-b border-kayama-line sticky top-0 z-30">
                <div className="container-k flex items-center justify-between py-4">
                    <Link to="/admin" className="flex items-center gap-3">
                        <img src={LOGO} alt="Kayama Admin" className="h-10 w-auto" />
                        <span className="font-heading text-2xl text-kayama-brown hidden md:inline">Admin</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link to="/admin/reviews" className="link-gold text-[11px]" data-testid="admin-reviews-link">Avis clients</Link>
                        <Link to="/admin/content" className="link-gold text-[11px]" data-testid="admin-content-link">Modifier le site</Link>
                        <Link to="/" className="link-gold text-[11px]" data-testid="admin-view-site">Voir le site</Link>
                        <span className="text-xs text-kayama-brown-2 hidden md:inline">{user?.email}</span>
                        <button onClick={handleLogout} className="btn-outline-k" data-testid="admin-logout">
                            <LogOut size={14} /> Déconnexion
                        </button>
                    </div>
                </div>
            </header>

            <div className="container-k py-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                    <div>
                        <p className="overline">Tableau de bord</p>
                        <h1 className="mt-2 font-heading text-4xl md:text-5xl font-light text-kayama-brown">Demandes de devis</h1>
                    </div>
                    <button onClick={load} className="btn-ghost-k" data-testid="admin-refresh">
                        <RefreshCw size={14} /> Rafraîchir
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
                    <StatCard label="Total" value={stats.total} highlight />
                    {STATUSES.map((s) => (
                        <StatCard key={s.value} label={s.label} value={stats.by_status[s.value] || 0} />
                    ))}
                </div>

                {/* Filters */}
                <div className="bg-kayama-paper border border-kayama-line p-4 flex flex-col md:flex-row gap-4 items-stretch md:items-center mb-6">
                    <div className="flex items-center gap-2 flex-1 border-b md:border-b-0 md:border-r md:pr-4 border-kayama-line pb-3 md:pb-0">
                        <Search size={16} className="text-kayama-brown-3" />
                        <input
                            placeholder="Rechercher par nom, email, événement…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="flex-1 bg-transparent focus:outline-none text-sm placeholder:text-kayama-brown-3"
                            data-testid="admin-search"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="overline">Filtre :</span>
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="w-40 rounded-none border-kayama-line" data-testid="admin-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous</SelectItem>
                                {STATUSES.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-kayama-paper border border-kayama-line overflow-hidden">
                    {loading ? (
                        <div className="p-10 text-center text-kayama-brown-2">Chargement…</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-10 text-center text-kayama-brown-2" data-testid="admin-empty">Aucune demande pour le moment.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="admin-devis-table">
                                <thead className="bg-kayama-muted">
                                    <tr className="text-left">
                                        <Th>Date</Th>
                                        <Th>Nom</Th>
                                        <Th>Événement</Th>
                                        <Th className="text-right">Convives</Th>
                                        <Th>Date événement</Th>
                                        <Th>Statut</Th>
                                        <Th className="text-right">Actions</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((it) => (
                                        <tr key={it.id} className="border-t border-kayama-line hover:bg-kayama-cream/50 cursor-pointer" onClick={() => setSelected(it)} data-testid={`devis-row-${it.id}`}>
                                            <Td>{formatDate(it.created_at)}</Td>
                                            <Td>
                                                <div className="font-medium text-kayama-brown">{it.name}</div>
                                                <div className="text-xs text-kayama-brown-3">{it.email}</div>
                                            </Td>
                                            <Td>{it.event_type}</Td>
                                            <Td className="text-right">{it.guests}</Td>
                                            <Td>{it.event_date || "—"}</Td>
                                            <Td><StatusBadge status={it.status} /></Td>
                                            <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="inline-flex items-center gap-2">
                                                    <Select value={it.status} onValueChange={(v) => changeStatus(it.id, v)}>
                                                        <SelectTrigger className="h-8 w-28 rounded-none text-xs border-kayama-line" data-testid={`status-select-${it.id}`}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {STATUSES.map((s) => (
                                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <button onClick={() => remove(it.id)} className="p-2 text-kayama-brown-3 hover:text-kayama-pink" data-testid={`delete-${it.id}`} aria-label="Supprimer">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Detail modal */}
            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-2xl rounded-none border-kayama-line" data-testid="devis-detail-dialog">
                    <DialogHeader>
                        <p className="overline">Demande de devis</p>
                        <DialogTitle className="font-heading text-3xl font-light text-kayama-brown">
                            {selected?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-6 pt-2">
                            <div className="flex items-center justify-between">
                                <StatusBadge status={selected.status} />
                                <span className="text-xs text-kayama-brown-3">Reçue le {formatDate(selected.created_at)}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                <Info icon={Mail} label="Email" value={selected.email} />
                                <Info icon={Phone} label="Téléphone" value={selected.phone} />
                                <Info icon={Calendar} label="Type" value={selected.event_type} />
                                <Info icon={Calendar} label="Date événement" value={selected.event_date || "—"} />
                                <Info icon={Users} label="Convives" value={selected.guests} />
                                <Info icon={Users} label="Budget" value={selected.budget || "—"} />
                            </div>
                            {selected.message && (
                                <div className="bg-kayama-muted p-5 border-l-2 border-kayama-pink">
                                    <p className="overline">Message</p>
                                    <p className="mt-2 text-kayama-brown leading-relaxed whitespace-pre-line">{selected.message}</p>
                                </div>
                            )}
                            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-kayama-line">
                                <div className="flex items-center gap-2">
                                    <span className="overline">Statut :</span>
                                    <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v)}>
                                        <SelectTrigger className="h-9 w-36 rounded-none border-kayama-line" data-testid="detail-status-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STATUSES.map((s) => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <a href={`mailto:${selected.email}`} className="btn-outline-k" data-testid="detail-email-btn">Répondre par email</a>
                                    <button onClick={() => remove(selected.id)} className="btn-outline-k" data-testid="detail-delete-btn">
                                        <Trash2 size={14} /> Supprimer
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function StatCard({ label, value, highlight }) {
    return (
        <div className={`bg-kayama-paper border ${highlight ? "border-kayama-pink" : "border-kayama-line"} p-5`}>
            <p className="overline">{label}</p>
            <p className={`mt-2 font-heading text-4xl ${highlight ? "text-kayama-pink" : "text-kayama-brown"}`}>{value}</p>
        </div>
    );
}

function Th({ children, className = "" }) {
    return <th className={`px-4 py-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-kayama-brown-2 ${className}`}>{children}</th>;
}

function Td({ children, className = "", onClick }) {
    return <td onClick={onClick} className={`px-4 py-4 text-kayama-brown ${className}`}>{children}</td>;
}

function Info({ icon: Icon, label, value }) {
    return (
        <div>
            <p className="overline flex items-center gap-2"><Icon size={12} className="text-kayama-pink" /> {label}</p>
            <p className="mt-1 text-kayama-brown break-words">{value}</p>
        </div>
    );
}
